import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, Col, Form, Input, Radio, Row, Select } from 'antd';
import { formRules, requireText } from 'common/const/common.const';
import { ECommonFormType } from 'common/models/commonModel';
import { useFormMapper } from 'common/helpers/form.helper';
import { useIsContentRestricted } from 'common/hooks/useIsContentRestricted';
import { ConfirmationModal } from 'common/components/ConfirmationModal';
import { useAppSelector } from 'app/store/store.hooks';
import { setCategoryModelError, setCategoryModelParams } from 'app/store/reducers/category.reducer';
import { history } from 'app/store';
import {
  ECategoryDrinkTemperature,
  ECategoryMealBuilderCategoryType,
  ECategoryMealBuilderCategoryTypeText,
  ECategoryMealBuilderRole,
  ECategoryType,
  ECategoryTypeText,
  ICategoryCreateData,
  ICategoryUpdateData,
} from 'entities/Category/Category.models';
import { ECommonEntityStatus, ECommonEntityStatusText } from 'entities/Bitecoin/Bitecoin.models';

const getMealBuilderFields = (
  categoryType?: ECategoryMealBuilderCategoryType
): { mealBuilderRole?: ECategoryMealBuilderRole; drinkTemperature?: ECategoryDrinkTemperature } => {
  switch (categoryType) {
    case ECategoryMealBuilderCategoryType.Side: {
      return { mealBuilderRole: ECategoryMealBuilderRole.Side };
    }
    case ECategoryMealBuilderCategoryType.ColdDrinks: {
      return { mealBuilderRole: ECategoryMealBuilderRole.Drink, drinkTemperature: ECategoryDrinkTemperature.Cold };
    }
    case ECategoryMealBuilderCategoryType.HotDrinks: {
      return { mealBuilderRole: ECategoryMealBuilderRole.Drink, drinkTemperature: ECategoryDrinkTemperature.Hot };
    }
    default: {
      return {};
    }
  }
};

interface IComponentProps {
  createCategory: (data: ICategoryCreateData) => void;
  updateCategory: (data: ICategoryUpdateData) => void;
  typeForm: ECommonFormType;
}

const CategoryFormComponent: React.FC<IComponentProps> = (props) => {
  const dispatch = useDispatch();
  const { categoryModel, categoryModelLoading, categoryModelParams, categoryModelError } = useAppSelector(
    (state) => state.category
  );
  const canManageCategory = !useIsContentRestricted();
  const { createCategory, updateCategory, typeForm } = props;
  const categoryId = categoryModel?.id;
  const isCreateForm = typeForm === ECommonFormType.Add;
  const isFieldDisabled = categoryModelLoading || !canManageCategory;
  const { fields, setField } = useFormMapper(
    // 'categoryType' we will set separately in useEffect
    ['name', 'type', 'status', 'isMealBuilder'],
    categoryModel,
    categoryModelParams,
    categoryModelError
  );

  const [confirmationModalData, setConfirmationModalData] = useState<{ isVisible: boolean; data?: any }>({
    isVisible: false,
  });

  const showModal = (data?: any) => setConfirmationModalData({ isVisible: true, data });
  const hideModal = () => setConfirmationModalData({ isVisible: false, data: null });

  const addCategory = (values: any) => {
    const { name, type, status, isMealBuilder, categoryType } = values;
    const { mealBuilderRole, drinkTemperature } = getMealBuilderFields(categoryType);

    const data: ICategoryCreateData = {
      name,
      type,
      status,
      isMealBuilder,
      mealBuilderRole,
      drinkTemperature,
    };

    createCategory && createCategory(data);
  };

  const editCategory = (values?: any) => {
    const updateCategoryData = values ? values : confirmationModalData.data || null;

    if (!updateCategoryData) {
      return;
    }
    const { name, status, isMealBuilder, categoryType } = updateCategoryData;
    const { mealBuilderRole, drinkTemperature } = getMealBuilderFields(categoryType);

    if (categoryId) {
      const data: ICategoryUpdateData = {
        name,
        status,
        isMealBuilder,
        mealBuilderRole,
        drinkTemperature,
        id: categoryId,
      };

      updateCategory && updateCategory(data);
    }
  };

  const onSubmit = (values: any) => {
    if (isCreateForm) {
      addCategory(values);
    } else {
      if (values.status === ECommonEntityStatus.Archived) {
        showModal(values);
      } else {
        editCategory(values);
      }
    }
  };

  const onValuesChange = (changedValues: any) => {
    if ('isMealBuilder' in changedValues) {
      setField({ name: 'isMealBuilder', value: changedValues.isMealBuilder });
      if (changedValues.isMealBuilder === true) {
        setField({ name: 'type', value: ECategoryType.DisableAddOns });
      }
    }
  };

  const clearFormData = () => {
    dispatch(setCategoryModelError(null));
    dispatch(setCategoryModelParams(null));
  };

  const onCancel = () => {
    clearFormData();

    history.go(-1);
  };

  useEffect(() => {
    return () => {
      console.log('>>> cat form unmount');

      clearFormData();
    };
  }, []);

  useEffect(() => {
    if (!categoryModelLoading && categoryModel) {
      const { mealBuilderRole, drinkTemperature } = categoryModel;
      let categoryType = undefined;

      if (mealBuilderRole === ECategoryMealBuilderRole.Side) {
        categoryType = ECategoryMealBuilderCategoryType.Side;
      } else if (mealBuilderRole === ECategoryMealBuilderRole.Drink) {
        if (drinkTemperature === ECategoryDrinkTemperature.Cold) {
          categoryType = ECategoryMealBuilderCategoryType.ColdDrinks;
        } else if (drinkTemperature === ECategoryDrinkTemperature.Hot) {
          categoryType = ECategoryMealBuilderCategoryType.HotDrinks;
        }
      }

      setField({ name: 'categoryType', value: categoryType });
    }
  }, [categoryModelLoading, categoryModel]);

  return (
    <>
      <ConfirmationModal
        isVisible={confirmationModalData.isVisible}
        title="Are you sure you want to archive this category?"
        mainText="It will be hidden from all active menus and default views."
        onSubmit={() => editCategory()}
        onCancel={hideModal}
        submitText="Yes, Archive"
        loading={categoryModelLoading}
      />
      <Form className="mt-5" onFinish={onSubmit} fields={fields} layout="vertical" onValuesChange={onValuesChange}>
        <Form.Item
          rules={[
            formRules.required,
            {
              min: 2,
              message: 'Category name must be longer than or equal 2 characters',
            },
            {
              max: 128,
              message: 'Category name must be shorter than or equal 128 characters',
            },
          ]}
          name="name"
          label="Category name"
        >
          <Input name="name" type="text" placeholder="Category name" disabled={isFieldDisabled} />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.isMealBuilder !== currentValues.isMealBuilder}
        >
          {({ getFieldValue }) => (
            <Form.Item
              rules={[{ required: true, message: requireText }]}
              name="type"
              label="Add–ons"
            >
              <Radio.Group disabled={isFieldDisabled || !isCreateForm || getFieldValue('isMealBuilder') === true}>
                <Radio value={ECategoryType.DisableAddOns} className="d-block mb-3">
                  {ECategoryTypeText.DisableAddOns}
                </Radio>
                <Radio value={ECategoryType.EnableAddOns} className="d-block mb-3">
                  {ECategoryTypeText.EnableAddOns}
                </Radio>
                <Radio value={ECategoryType.IsAddOns} className="d-block">
                  {ECategoryTypeText.IsAddOns}
                </Radio>
              </Radio.Group>
            </Form.Item>
          )}
        </Form.Item>
        <Form.Item
          rules={[
            {
              required: true,
              message: requireText,
            },
          ]}
          label="Category Status"
          name="status"
        >
          <Radio.Group disabled={isFieldDisabled}>
            <Radio value={ECommonEntityStatus.Active} className="d-block mb-3">
              {ECommonEntityStatusText.Active}
            </Radio>
            <Radio value={ECommonEntityStatus.Inactive} className="d-block mb-3">
              {ECommonEntityStatusText.Inactive}
            </Radio>
            <Radio value={ECommonEntityStatus.Archived} className="d-block">
              {ECommonEntityStatusText.Archived}
            </Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="isMealBuilder"
          valuePropName="checked"
          extra="Check this checkbox to enable this category for the Meal Builder functionality in addition to your asset menu"
        >
          <Checkbox disabled={isFieldDisabled}>Meal Builder category</Checkbox>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.isMealBuilder !== currentValues.isMealBuilder}
        >
          {({ getFieldValue }) =>
            getFieldValue('isMealBuilder') === true ? (
              <Row>
                <Col span={12}>
                  <Form.Item
                    rules={[formRules.required]}
                    name="categoryType"
                    label="Meal Builder category type"
                    preserve={false}
                  >
                    <Select placeholder="Choose meal builder category type" className="width-full" disabled={isFieldDisabled}>
                      <Select.Option
                        value={ECategoryMealBuilderCategoryType.Side}
                        label={ECategoryMealBuilderCategoryTypeText.Side}
                      >
                        {ECategoryMealBuilderCategoryTypeText.Side}
                      </Select.Option>
                      <Select.Option
                        value={ECategoryMealBuilderCategoryType.ColdDrinks}
                        label={ECategoryMealBuilderCategoryTypeText.ColdDrinks}
                      >
                        {ECategoryMealBuilderCategoryTypeText.ColdDrinks}
                      </Select.Option>
                      <Select.Option
                        value={ECategoryMealBuilderCategoryType.HotDrinks}
                        label={ECategoryMealBuilderCategoryTypeText.HotDrinks}
                      >
                        {ECategoryMealBuilderCategoryTypeText.HotDrinks}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            ) : null
          }
        </Form.Item>

        <Row className="flex-noWrap" justify="space-between" align="middle">
          <Col span={12}>
            <Row className="flex-noWrap">
              <Form.Item className="mb-0 mr-5">
                <Button
                  htmlType="submit"
                  type="primary"
                  disabled={categoryModelLoading || !canManageCategory}
                  loading={categoryModelLoading}
                  title={isCreateForm ? 'Add' : 'Save'}
                >
                  {isCreateForm ? 'Add' : 'Save'}
                </Button>
              </Form.Item>

              <Button onClick={onCancel} disabled={categoryModelLoading} loading={categoryModelLoading} title="Cancel">
                Cancel
              </Button>
            </Row>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export const CategoryForm = CategoryFormComponent;
